import { FormTabsTypes } from "src/constants";

// Column IDs need to be URL-friendly.
// They should not include underscores as the delimiter in the URL is underscore.
export enum ColumnId {
  ID = "id",
  AttendanceStatus = "attendance-status",
  AttendingSchool = "attending-school",
  AttendingSchoolId = "attending-school-id",
  AttendingSchoolReferenceId = "attending-school-reference-id",
  FormSchoolRowId = "form-school-row-id",
  Grade = "apl-grade",
  GuardianFirstName = "guardian-first-name",
  GuardianMiddleName = "guardian-middle-name",
  GuardianLastName = "guardian-last-name",
  GuardianId = "guardian-id",
  GuardianAddress = "guardian-address",
  GuardianEmail = "guardian-email",
  GuardianPhone = "guardian-phone",
  GuardianLanguage = "guardian-language",
  Program = "program-name",
  ProgramId = "program-id",
  Question = "question",
  School = "school",
  SchoolId = "school-id",
  SchoolRank = "school-rank",
  EligibilityOverride = "eligibility-override",
  OverrideReason = "override-reason",
  SchoolReferenceId = "school-reference-id",
  SchoolSubmittedDate = "school-submitted-date",
  Status = "status",
  StatusDate = "status-date",
  StudentFirstName = "student-first-name",
  StudentMiddleName = "student-middle-name",
  StudentLastName = "student-last-name",
  StudentId = "student-id",
  StudentAddress = "student-address",
  StudentDateOfBirth = "student-date-of-birth",
  SubmittedDate = "submitted-date",
  SubStatus = "sub-status",
  SubStatusDate = "sub-status-date",
  Tags = "tags",
  Tiebreaker = "tiebreaker",
  Verifications = "verifications",
  Errors = "errors",
  Visibility = "is-hidden-from-parent",
  WaitlistPosition = "position",
  LotteryNumber = "lottery-number",
  LotteryPosition = "lottery-position",
}

// This is the ORDERED list of all columns that can be shown in the form list table.
// Additional columns may be present for dynamic columns like tags and answers.
// NOTE: If adding to this list, also check if should add to:
// src/scenes/orgAdmin/forms/components/useFormsList.ts > NON_BASIC_LIST_FORMS_COLUMNS
export const ALL_FORM_LIST_COLUMNS: ColumnId[] = [
  ColumnId.ID,
  ColumnId.WaitlistPosition,
  ColumnId.LotteryPosition,
  ColumnId.StudentFirstName,
  ColumnId.StudentMiddleName,
  ColumnId.StudentLastName,
  ColumnId.StudentId,
  ColumnId.StudentDateOfBirth,
  ColumnId.StudentAddress,
  ColumnId.AttendanceStatus,
  ColumnId.AttendingSchool,
  ColumnId.AttendingSchoolId,
  ColumnId.AttendingSchoolReferenceId,
  ColumnId.SchoolRank,
  ColumnId.School,
  ColumnId.SchoolId,
  ColumnId.EligibilityOverride,
  ColumnId.OverrideReason,
  ColumnId.SchoolReferenceId,
  ColumnId.SchoolSubmittedDate,
  ColumnId.Grade,
  ColumnId.Status,
  ColumnId.StatusDate,
  ColumnId.SubmittedDate,
  ColumnId.SubStatus,
  ColumnId.SubStatusDate,
  ColumnId.Errors,
  ColumnId.Program,
  ColumnId.ProgramId,
  ColumnId.Tags,
  ColumnId.Verifications,
  ColumnId.Tiebreaker,
  ColumnId.GuardianFirstName,
  ColumnId.GuardianMiddleName,
  ColumnId.GuardianLastName,
  ColumnId.GuardianId,
  ColumnId.GuardianAddress,
  ColumnId.GuardianEmail,
  ColumnId.GuardianPhone,
  ColumnId.GuardianLanguage,
  ColumnId.Question,
];

// Reference as of July 2025: https://docs.google.com/spreadsheets/d/1JaWyN5IC12AytEFczdlSeQ5-xvTVC5nToM_IfrtEPj8/edit?usp=sharing
// These are the default columns that will be shown in the table for each tab, that can be de-selected.
// Columns that cannot be de-selected are not included here.
export const defaultColumnsByTabType: { [key in FormTabsTypes]: ColumnId[] } = {
  [FormTabsTypes.All]: [
    ColumnId.StudentFirstName,
    ColumnId.StudentLastName,
    ColumnId.StudentId,
    ColumnId.SchoolRank,
    ColumnId.School,
    ColumnId.Grade,
    ColumnId.Status,
    ColumnId.StatusDate,
    ColumnId.SubStatus,
    ColumnId.SubStatusDate,
    ColumnId.Tags,
  ],
  [FormTabsTypes.Submissions]: [
    ColumnId.ID,
    ColumnId.StudentFirstName,
    ColumnId.StudentLastName,
    ColumnId.StudentId,
    ColumnId.SchoolRank,
    ColumnId.School,
    ColumnId.Grade,
    ColumnId.Status,
    ColumnId.StatusDate,
    ColumnId.Tags,
    ColumnId.Verifications,
  ],
  [FormTabsTypes.Waitlists]: [
    ColumnId.WaitlistPosition,
    ColumnId.StudentFirstName,
    ColumnId.StudentLastName,
    ColumnId.StudentId,
    ColumnId.School,
    ColumnId.Grade,
    ColumnId.SubStatus,
    ColumnId.SubStatusDate,
    ColumnId.Tags,
    ColumnId.Tiebreaker,
  ],
  [FormTabsTypes.Offers]: [
    ColumnId.StudentFirstName,
    ColumnId.StudentLastName,
    ColumnId.StudentId,
    ColumnId.School,
    ColumnId.Grade,
    ColumnId.SubStatus,
    ColumnId.SubStatusDate,
    ColumnId.Tags,
  ],
  [FormTabsTypes.FormListImports]: [
    ColumnId.SchoolId,
    ColumnId.StudentFirstName,
    ColumnId.StudentLastName,
    ColumnId.StudentId,
    ColumnId.SchoolRank,
    ColumnId.School,
    ColumnId.Grade,
    ColumnId.SubStatus,
    ColumnId.SubStatusDate,
    ColumnId.Tags,
  ],
  [FormTabsTypes.DropoffForms]: [
    ColumnId.StudentFirstName,
    ColumnId.StudentLastName,
    ColumnId.StudentId,
    ColumnId.School,
    ColumnId.Grade,
    ColumnId.Status,
    ColumnId.StatusDate,
    ColumnId.SubStatus,
    ColumnId.SubStatusDate,
    ColumnId.Tags,
  ],
};

// Backward compatibility mapping
export const deprecatedColumnKeyToCurrentColumnKey: Record<string, string> = {
  apl_grade: ColumnId.Grade,
  school_id: ColumnId.SchoolId,
  school_rank: ColumnId.SchoolRank,
  status_date: ColumnId.StatusDate,
  student: ColumnId.StudentFirstName,
  student_first_name: ColumnId.StudentFirstName,
  student_last_name: ColumnId.StudentLastName,
  student_id: ColumnId.StudentId,
  "sub-status-form-school-rank": ColumnId.SubStatusDate,
  sub_status: ColumnId.SubStatus,
  sub_status_date: ColumnId.SubStatusDate,
  is_hidden_from_parent: ColumnId.Visibility,
  lottery_number: ColumnId.LotteryNumber,
};
